(() => {
  const shared = window.PawuniThemeHotfixShared;
  if (!shared) return;

  const {
    DISCOUNT_PRICE_APP_SELECTOR,
    JDGM_PREVIEW_BADGE_SELECTOR,
    PRODUCT_DETAILS_SELECTOR,
    applySyntheticJudgeMePreviewCounts,
    bindJudgeMeAndTeeinblueEvents,
    createRafThrottled,
    extractFirstInteger,
    hasBodyClassMatch,
    isProductContext,
    mutationsContainAddedNode,
    mutationsHaveAddedNodes,
    nodeMatchesOrContains,
    normalizeInlineText,
    removeAll,
    removeClassFromAll,
    resolvePdpLayoutMode,
    runWithDelays,
    seededIntegerInRange,
    unwrapAll,
  } = shared;

  const PAWUNI_REVIEW_STAR_SVG =
    '<svg class="pawuni-icon pawuni-icon--rating-star" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l2.5 5.5 6 .5-4.5 4 1.5 6L12 16l-5.5 3 1.5-6-4.5-4 6-.5L12 3z" fill="currentColor"/></svg>';

  const applyPdpLayoutModeClass = () => {
    const mode = resolvePdpLayoutMode();

    document.documentElement.setAttribute('data-pdp-layout-mode', mode);
    window.PawuniPdpLayoutMode = mode;

    if (!document.body) return;

    document.body.dataset.pdpLayoutMode = mode;
    document.body.classList.remove('pdp-layout-mode-1', 'pdp-layout-mode-2', 'pdp-layout-mode-3');
    document.body.classList.add(`pdp-layout-mode-${mode}`);
  };

  const PDP_OTHER_PRODUCTS_REVIEWS_BASELINE_UTC = Date.UTC(2026, 3, 6);
  const PDP_OTHER_PRODUCTS_REVIEWS_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  const PDP_OTHER_PRODUCTS_REVIEWS_BASE_COUNT = 10000;

  const getPdpOtherProductsReviewWeeksSinceBaseline = () => {
    return Math.max(0, Math.floor((Date.now() - PDP_OTHER_PRODUCTS_REVIEWS_BASELINE_UTC) / PDP_OTHER_PRODUCTS_REVIEWS_WEEK_MS));
  };

  const getPdpOtherProductsReviewGrowth = (seedBase) => {
    let total = 0;
    const weeks = getPdpOtherProductsReviewWeeksSinceBaseline();

    for (let weekIndex = 0; weekIndex < weeks; weekIndex += 1) {
      total += seededIntegerInRange(`${seedBase}:${weekIndex}`, 10000, 20000);
    }

    return total;
  };

  const formatIntegerWithCommas = (value) => {
    return Number(value || 0).toLocaleString('en-US');
  };

  const updateJudgeMeOtherProductsReviewsHeading = (scope = document) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    const productSeed =
      normalizeInlineText(document.querySelector('.jdgm-preview-badge[data-id]')?.getAttribute('data-id')) ||
      normalizeInlineText(window.ShopifyAnalytics?.meta?.product?.id) ||
      normalizeInlineText(window.location.pathname) ||
      'pdp';

    const candidates = Array.from(scope.querySelectorAll('#judgeme_product_reviews *')).filter((node) => {
      if (!(node instanceof Element)) return false;

      const text = normalizeInlineText(node.textContent || '');
      if (!/^Reviews for other products\b/i.test(text)) return false;
      if (text.length > 90) return false;

      return true;
    });

    candidates.forEach((node) => {
      const sourceText = node.dataset.pawuniOtherProductsReviewsOriginalText || normalizeInlineText(node.textContent || '');
      const realCount =
        Number(node.dataset.pawuniOtherProductsReviewsRealCount || 0) ||
        extractFirstInteger(sourceText) ||
        0;

      node.dataset.pawuniOtherProductsReviewsOriginalText = sourceText;
      node.dataset.pawuniOtherProductsReviewsRealCount = String(realCount);

      const displayCount =
        realCount +
        PDP_OTHER_PRODUCTS_REVIEWS_BASE_COUNT +
        getPdpOtherProductsReviewGrowth(`pawuni-pdp-other-products-reviews:${productSeed}`);

      if (/\(\s*[\d,]+\s*\)/.test(sourceText)) {
        node.textContent = sourceText.replace(/\(\s*[\d,]+\s*\)/, `(${formatIntegerWithCommas(displayCount)})`);
        return;
      }

      node.textContent = `${sourceText} (${formatIntegerWithCommas(displayCount)})`;
    });
  };

  const initSyntheticJudgeMePreviewCounts = () => {
    if (!isProductContext(JDGM_PREVIEW_BADGE_SELECTOR)) return;

    const run = () => applySyntheticJudgeMePreviewCounts(document);
    const rerunThrottled = createRafThrottled(run);

    run();

    if (window.__pawuniSyntheticJudgeMeInitialized) return;
    window.__pawuniSyntheticJudgeMeInitialized = true;

    runWithDelays(run, [600, 1800, 3600]);

    const observer = new MutationObserver((mutations) => {
      if (!mutationsContainAddedNode(mutations, `.jdgm-preview-badge, ${PRODUCT_DETAILS_SELECTOR}`)) return;
      rerunThrottled();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    bindJudgeMeAndTeeinblueEvents(rerunThrottled);
  };

  const initJudgeMeOtherProductsReviewsHeading = () => {
    if (!isProductContext('#judgeme_product_reviews')) return;

    const run = () => updateJudgeMeOtherProductsReviewsHeading(document);
    const rerunThrottled = createRafThrottled(run);

    run();
    runWithDelays(run, [300, 900, 1800, 3600]);

    if (window.__pawuniJudgeMeOtherProductsReviewsHeadingBound) return;
    window.__pawuniJudgeMeOtherProductsReviewsHeadingBound = true;

    const reviewRoot = document.querySelector('#judgeme_product_reviews') || document.body;
    const observer = new MutationObserver((mutations) => {
      if (!mutationsContainAddedNode(mutations, '#judgeme_product_reviews')) return;
      rerunThrottled();
    });

    observer.observe(reviewRoot, { childList: true, subtree: true });
    bindJudgeMeAndTeeinblueEvents(rerunThrottled);
  };

  const toLower = (value) =>
    String(value || '')
      .toLowerCase()
      .trim();

  const isPicturesFirstValue = (value) => {
    const normalizedValue = toLower(value);

    return (
      normalizedValue.includes('pictures_first') ||
      normalizedValue.includes('pictures-first') ||
      normalizedValue.includes('pictures first') ||
      normalizedValue.includes('photos_first') ||
      normalizedValue.includes('photos-first') ||
      normalizedValue.includes('photos first')
    );
  };

  const isPicturesFirstText = (value) => {
    const normalizedValue = toLower(value);

    return (
      normalizedValue.includes('pictures first') ||
      normalizedValue.includes('photos first') ||
      normalizedValue.includes('picture first') ||
      normalizedValue.includes('photo first')
    );
  };

  const applyInlineStyles = (element, styles) => {
    if (!(element instanceof Element)) return;

    Object.entries(styles).forEach(([property, value]) => {
      element.style.setProperty(property, value, 'important');
    });
  };

  const closeJudgeMeCustomSortDropdowns = (exceptControl = null) => {
    document.querySelectorAll('#judgeme_product_reviews .pawuni-jdgm-sort-control.is-open').forEach((control) => {
      if (control === exceptControl) return;

      control.classList.remove('is-open');
      control.querySelector('.pawuni-jdgm-sort-trigger')?.setAttribute('aria-expanded', 'false');
      const menu = control.querySelector('.pawuni-jdgm-sort-menu');
      if (menu instanceof HTMLElement) menu.hidden = true;
    });
  };

  const getJudgeMeSortOptionLabel = (option) => {
    const label = normalizeInlineText(option?.textContent || option?.text || '');
    return isPicturesFirstValue(option?.value) || isPicturesFirstText(label) ? 'Pictures first' : label;
  };

  const getJudgeMeSelectedSortOption = (select) => {
    return select.selectedOptions?.[0] || select.options?.[select.selectedIndex] || select.options?.[0] || null;
  };

  const syncJudgeMeCustomSortControl = (select) => {
    const sortFilter = select.closest('.jm-sort-filter, .jdgm-sort-dropdown-wrapper');
    const control = sortFilter?.querySelector('.pawuni-jdgm-sort-control');
    if (!control) return;

    const selectedOption = getJudgeMeSelectedSortOption(select);
    const selectedLabel = getJudgeMeSortOptionLabel(selectedOption) || 'Pictures first';

    const label = control.querySelector('.pawuni-jdgm-sort-trigger-label');
    if (label) label.textContent = selectedLabel;

    control.querySelectorAll('.pawuni-jdgm-sort-option').forEach((button) => {
      const isSelected = button.dataset.value === (selectedOption?.value || select.value || '');
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  };

  const enhanceJudgeMeSortSelect = (select) => {
    if (!(select instanceof HTMLSelectElement)) return;

    const sortFilter = select.closest('.jm-sort-filter, .jdgm-sort-dropdown-wrapper');
    if (!(sortFilter instanceof Element)) return;

    const options = Array.from(select.options || []).filter((option) => normalizeInlineText(option.text || option.textContent || ''));
    if (options.length < 2) return;

    sortFilter.classList.add('pawuni-jdgm-sort-enhanced');
    select.classList.add('pawuni-jdgm-native-sort-select');
    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;

    let control = sortFilter.querySelector('.pawuni-jdgm-sort-control');
    if (!control) {
      control = document.createElement('div');
      control.className = 'pawuni-jdgm-sort-control';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'pawuni-jdgm-sort-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');

      const label = document.createElement('span');
      label.className = 'pawuni-jdgm-sort-trigger-label';
      trigger.appendChild(label);

      const menu = document.createElement('div');
      menu.className = 'pawuni-jdgm-sort-menu';
      menu.setAttribute('role', 'listbox');
      menu.hidden = true;

      control.append(trigger, menu);
      sortFilter.appendChild(control);
    }

    const trigger = control.querySelector('.pawuni-jdgm-sort-trigger');
    const menu = control.querySelector('.pawuni-jdgm-sort-menu');
    if (!(trigger instanceof HTMLButtonElement) || !(menu instanceof HTMLElement)) return;

    menu.textContent = '';
    options.forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'pawuni-jdgm-sort-option';
      item.dataset.value = option.value;
      item.setAttribute('role', 'option');
      item.textContent = getJudgeMeSortOptionLabel(option);
      item.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        select.value = option.value;
        if (select.value !== option.value) select.selectedIndex = Array.from(select.options || []).indexOf(option);
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        syncJudgeMeCustomSortControl(select);
        closeJudgeMeCustomSortDropdowns();
      });
      menu.appendChild(item);
    });

    if (!trigger.dataset.pawuniJdgmSortBound) {
      trigger.dataset.pawuniJdgmSortBound = 'true';
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const nextOpenState = !control.classList.contains('is-open');
        closeJudgeMeCustomSortDropdowns(control);
        control.classList.toggle('is-open', nextOpenState);
        trigger.setAttribute('aria-expanded', nextOpenState ? 'true' : 'false');
        menu.hidden = !nextOpenState;
      });
    }

    if (!select.dataset.pawuniJdgmSortSelectBound) {
      select.dataset.pawuniJdgmSortSelectBound = 'true';
      select.addEventListener('change', () => syncJudgeMeCustomSortControl(select));
    }

    applyInlineStyles(select, {
      height: '1px',
      inset: '0 auto auto 0',
      opacity: '0',
      'pointer-events': 'none',
      position: 'absolute',
      width: '1px',
    });

    syncJudgeMeCustomSortControl(select);
  };

  const bindJudgeMeCustomSortDismissal = () => {
    if (window.__pawuniJudgeMeCustomSortDismissalBound) return;
    window.__pawuniJudgeMeCustomSortDismissalBound = true;

    document.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('#judgeme_product_reviews .pawuni-jdgm-sort-control')) return;
      closeJudgeMeCustomSortDropdowns();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeJudgeMeCustomSortDropdowns();
    });
  };

  const normalizeJudgeMePicturesFirstLabel = (scope = document) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    scope.querySelectorAll('#judgeme_product_reviews .jm-sort-filter').forEach((filter) => {
      applyInlineStyles(filter, {
        display: 'inline-flex',
        'align-items': 'center',
        gap: '0',
        width: 'auto',
        'min-width': '0',
      });

      filter.querySelectorAll('.jm-sort-filter__icon').forEach((icon) => {
        icon.remove();
      });
    });

    scope.querySelectorAll('#judgeme_product_reviews .jm-sort-filter select, #judgeme_product_reviews .jdgm-sort-dropdown-wrapper select').forEach((select) => {
      const options = Array.from(select.options || []);
      const matchesPicturesFirst = options.some(
        (option) => isPicturesFirstValue(option.value) || isPicturesFirstText(option.text),
      );

      if (!matchesPicturesFirst) return;

      options.forEach((option) => {
        if (isPicturesFirstValue(option.value) || isPicturesFirstText(option.text)) {
          option.text = 'Pictures first';
        }
      });

      applyInlineStyles(select, {
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        appearance: 'none',
        background: 'transparent',
        'background-image': 'none',
        'background-position': 'initial',
        'background-repeat': 'no-repeat',
        'background-size': '0 0',
        border: '0',
        'box-shadow': 'none',
        margin: '0',
        padding: '0',
        'padding-right': '0',
        'text-indent': '0',
        'text-overflow': 'clip',
        width: 'auto',
        'min-width': '0',
        'max-width': 'none',
        position: 'static',
      });

      select.setAttribute('aria-label', 'Pictures first');
      select.dataset.pawuniJdgmPicturesFirstNormalized = 'true';
      enhanceJudgeMeSortSelect(select);
    });

    scope.querySelectorAll('#judgeme_product_reviews .jdgm-sort-dropdown-wrapper').forEach((wrapper) => {
      const directChildren = Array.from(wrapper.children).filter((node) => node instanceof Element);
      const filterControl = directChildren[0] || null;

      const candidates = Array.from(wrapper.querySelectorAll('button, [role="button"], span, div, a, label, select')).filter(
        (node) => {
          if (!(node instanceof Element)) return false;
          if (node === filterControl || filterControl?.contains(node)) return false;
          if (node.closest('.pawuni-jdgm-sort-control')) return false;

          const text = normalizeInlineText(node.textContent || '');
          return /(?:pictures|photos)/i.test(text) && /first/i.test(text);
        },
      );

      candidates.forEach((node) => {
        if (node.tagName === 'SELECT') {
          Array.from(node.options || []).forEach((option) => {
            if (isPicturesFirstValue(option.value) || isPicturesFirstText(option.text)) {
              option.text = 'Pictures first';
            }
          });
          node.setAttribute('aria-label', 'Pictures first');
          enhanceJudgeMeSortSelect(node);
          return;
        }

        node.textContent = 'Pictures first';
        node.setAttribute('aria-label', 'Pictures first');
        node.dataset.pawuniJdgmPicturesFirstNormalized = 'true';
      });
    });
  };

  const initJudgeMeReviewActionsNormalization = () => {
    if (!isProductContext('#judgeme_product_reviews')) return;

    const run = () => normalizeJudgeMePicturesFirstLabel(document);
    const rerunThrottled = createRafThrottled(run);

    bindJudgeMeCustomSortDismissal();
    run();
    runWithDelays(run, [300, 900, 1800, 3600]);

    if (window.__pawuniJudgeMeReviewActionsNormalized) return;
    window.__pawuniJudgeMeReviewActionsNormalized = true;

    const reviewRoot = document.querySelector('#judgeme_product_reviews') || document.body;
    const observer = new MutationObserver((mutations) => {
      if (!mutationsContainAddedNode(mutations, '#judgeme_product_reviews, .jdgm-sort-dropdown-wrapper, .jm-sort-filter')) return;
      rerunThrottled();
    });

    observer.observe(reviewRoot, { childList: true, subtree: true });
    bindJudgeMeAndTeeinblueEvents(rerunThrottled);
  };

  const JUDGE_ME_PRODUCT_REVIEWS_SELECTOR = '#judgeme_product_reviews';
  const JUDGE_ME_REVAMP_CARD_SELECTOR = '.jdgm-review-card';
  const JUDGE_ME_REVAMP_CARD_MEDIA_SELECTOR = '.jdgm-review-card__media-cover, .jdgm-review-card__highlight';
  const JUDGE_ME_LEGACY_CARD_MEDIA_SELECTOR = '.jdgm-rev__pics img';
  const PAWUNI_JUDGE_ME_PHOTO_RAIL_CLASS = 'pawuni-jdgm-photo-rail';
  const PAWUNI_JUDGE_ME_INSIGHT_PANEL_CLASS = 'pawuni-jdgm-insight-panel';

  const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

  const parseFirstDecimal = (value) => {
    const match = normalizeInlineText(value).match(/\b\d+(?:\.\d+)?\b/);
    return match ? Number(match[0]) : null;
  };

  const formatDecimalRating = (value) => {
    return clampNumber(Number(value) || 5, 0, 5).toFixed(1);
  };

  const getJudgeMeMetricDisplayRating = (summary, label) => {
    const average = clampNumber(Number(summary.averageRating) || 5, 4.8, 5);
    const baseTenths = Math.round(average * 10);
    const minimumTenths = Math.max(48, Math.min(50, baseTenths));
    const maximumTenths = 50;
    const pathnameSeed = normalizeInlineText(window.location.pathname) || 'pdp';
    const metricSeed = ['pawuni-jdgm-metric-rating', pathnameSeed, summary.reviewCount, label].join(':');
    const metricTenths = seededIntegerInRange(metricSeed, minimumTenths, maximumTenths);

    return (metricTenths / 10).toFixed(1);
  };

  const formatJudgeMeReviewNoun = (count) => {
    const numericCount = Number(count) || 0;
    return `${formatIntegerWithCommas(numericCount)} ${numericCount === 1 ? 'review' : 'reviews'}`;
  };

  const resolveJudgeMeReviewMedia = (card) => {
    if (!(card instanceof Element)) return null;

    const mediaRoot =
      card.querySelector(JUDGE_ME_REVAMP_CARD_MEDIA_SELECTOR) ||
      card.querySelector('.jdgm-rev__pics');
    const image = mediaRoot?.querySelector?.('img') || card.querySelector(JUDGE_ME_LEGACY_CARD_MEDIA_SELECTOR);

    if (!(image instanceof HTMLImageElement)) return null;

    const src = image.currentSrc || image.getAttribute('src') || image.dataset.src || image.dataset.originalSrc;
    if (!src) return null;

    const clickTarget =
      image.closest('[role="button"], button, a') ||
      mediaRoot?.closest?.('[role="button"], button, a') ||
      mediaRoot ||
      card;

    return { image, clickTarget };
  };

  const getJudgeMeReviewImageSrc = (image, width = null) => {
    if (!(image instanceof HTMLImageElement)) return '';

    const src = image.dataset.originalSrc || image.currentSrc || image.getAttribute('src') || image.dataset.src || image.src || '';
    if (!src || !width) return src;

    try {
      const url = new URL(src, window.location.origin);
      if (url.hostname.includes('judgeme') || url.hostname.includes('imgix') || url.searchParams.has('w')) {
        url.searchParams.set('auto', url.searchParams.get('auto') || 'format');
        url.searchParams.set('w', String(width));
      }
      return url.toString();
    } catch (_error) {
      return src.replace(/([?&])w=\d+/i, `$1w=${width}`);
    }
  };

  const getJudgeMeReviewCardName = (card) => {
    return normalizeInlineText(
      card.querySelector('.jdgm-review-card__name, .jdgm-rev__author')?.textContent ||
        card.querySelector('[class*="name"], [class*="author"]')?.textContent ||
        '',
    );
  };

  const getJudgeMeReviewCardRating = (card) => {
    if (!(card instanceof Element)) return null;

    const label = card.querySelector('.jm-star-rating[aria-label]')?.getAttribute('aria-label') || '';
    const labelRating = parseFirstDecimal(label);
    if (labelRating !== null) return clampNumber(labelRating, 0, 5);

    const stars = card.querySelectorAll('.jdgm-star.jdgm--on, .jm-star-rating__font-icon');
    return stars.length ? clampNumber(stars.length, 0, 5) : null;
  };

  const findJudgeMeTitlePreviewBadge = (root) => {
    const doc = root?.ownerDocument || document;
    const badges = Array.from(doc.querySelectorAll(JDGM_PREVIEW_BADGE_SELECTOR)).filter((badge) => !root?.contains(badge));

    return (
      badges.find((badge) =>
        badge.closest('.product-block--rating-and-badge, .product__details--product-page, .product__details, .product_section'),
      ) ||
      badges[0] ||
      null
    );
  };

  const getJudgeMePreviewBadgeRating = (badge) => {
    if (!(badge instanceof Element)) return null;

    return parseFirstDecimal(
      badge.dataset.pawuniJdgmDisplayRating ||
        badge.querySelector('.jdgm-prev-badge')?.dataset.pawuniDisplayRating ||
        badge.getAttribute('data-average-rating') ||
        badge.querySelector('[data-score]')?.getAttribute('data-score') ||
        badge.textContent ||
        '',
    );
  };

  const getJudgeMePreviewBadgeCount = (badge) => {
    if (!(badge instanceof Element)) return null;

    return (
      extractFirstInteger(badge.dataset.pawuniJdgmSyntheticReviewCount) ||
      extractFirstInteger(badge.getAttribute('data-number-of-reviews')) ||
      extractFirstInteger(badge.querySelector('.jdgm-prev-badge')?.getAttribute('data-number-of-reviews')) ||
      extractFirstInteger(badge.querySelector('.jdgm-prev-badge__text')?.textContent?.match(/([\d,]+)\s+reviews?/i)?.[1]) ||
      extractFirstInteger(badge.querySelector('.jdgm-prev-badge__text')?.textContent?.match(/\(\s*([\d,]+)\s*\)/)?.[1]) ||
      extractFirstInteger(badge.textContent?.match(/([\d,]+)\s+reviews?/i)?.[1]) ||
      extractFirstInteger(badge.textContent?.match(/\(\s*([\d,]+)\s*\)/)?.[1]) ||
      null
    );
  };

  const getJudgeMeReviewSummaryData = (root) => {
    const titlePreviewBadge = findJudgeMeTitlePreviewBadge(root);
    const averageText = normalizeInlineText(
      root.querySelector('.jm-average-rating-display, .jdgm-rev-widg__summary')?.textContent ||
        root.querySelector('.jdgm-prev-badge')?.getAttribute('data-average-rating') ||
        '',
    );

    const averageRating = getJudgeMePreviewBadgeRating(titlePreviewBadge) || parseFirstDecimal(averageText) || 5;
    const reviewCount =
      getJudgeMePreviewBadgeCount(titlePreviewBadge) ||
      extractFirstInteger(averageText.match(/([\d,]+)\s+reviews?/i)?.[1]) ||
      extractFirstInteger(root.querySelector('.jdgm-prev-badge')?.getAttribute('data-number-of-reviews')) ||
      extractFirstInteger(root.textContent?.match(/([\d,]+)\s+reviews?/i)?.[1]) ||
      root.querySelectorAll(JUDGE_ME_REVAMP_CARD_SELECTOR).length ||
      0;

    const reviewCards = Array.from(root.querySelectorAll(JUDGE_ME_REVAMP_CARD_SELECTOR));
    const ratings = reviewCards.map(getJudgeMeReviewCardRating).filter((rating) => rating !== null);
    const positiveCount = ratings.filter((rating) => rating >= 4).length;
    const recommendationPercent = ratings.length
      ? Math.round((positiveCount / ratings.length) * 100)
      : Math.round((clampNumber(averageRating, 0, 5) / 5) * 100);

    const text = normalizeInlineText(
      reviewCards
        .map((card) =>
          [
            card.querySelector('.jdgm-review-card__title, .jdgm-rev__title')?.textContent,
            card.querySelector('.jdgm-review-card__body, .jdgm-rev__body')?.textContent,
          ]
            .filter(Boolean)
            .join(' '),
        )
        .join(' '),
    );

    const photoCount =
      Number(root.dataset.pawuniJdgmPhotoCount || 0) ||
      reviewCards.filter((card) => Boolean(resolveJudgeMeReviewMedia(card))).length;

    return {
      averageRating: clampNumber(averageRating, 0, 5),
      photoCount,
      recommendationPercent: clampNumber(recommendationPercent, 0, 100),
      reviewCount,
      reviewText: text,
    };
  };

  const getJudgeMeReviewHighlights = (summary) => {
    const text = toLower(summary.reviewText);
    const seedBase = [
      'pawuni-jdgm-highlights',
      window.location.pathname,
      summary.reviewCount,
      formatDecimalRating(summary.averageRating),
    ].join(':');
    const rules = [
      [['Loved it', 'Happy buyer', 'Instant favorite', 'So loved'], /\blove[ds]?\b|fantastic|happy|favorite|go-to/],
      [['Gift-worthy', 'Perfect gift', 'Meaningful gift', 'Family favorite'], /\bgift|husband|friend|wife|grandma|mom|dad/],
      [['Great quality', 'Well made', 'Looks beautiful', 'Premium feel'], /\bquality|sturdy|great|perfect|excellent|beautiful/],
      [['Fast shipping', 'Arrived fast', 'Quick delivery', 'Smooth order'], /\bshipping|ship|received|order|delivery/],
      [['Personal touch', 'Custom favorite', 'Clear photo', 'Perfectly custom'], /\bpersonalized|custom|name|number|photo|picture/],
      [['Well packed', 'Safe packaging', 'Nicely packed'], /\bpackaging|packed|package/],
      [['Perfect size', 'Great fit', 'Right size'], /\bsize|oz|tumbler|fit/],
    ];
    const fallback = [
      'Loved it',
      'Gift-worthy',
      'Great quality',
      'Fast shipping',
      'Personal touch',
      'Well made',
      'Looks beautiful',
      'Perfect gift',
      'Happy buyer',
      'Quick delivery',
      'Clear photo',
      'Well packed',
      'Family favorite',
      'Premium feel',
      'Great service',
      'Recommend it',
    ];
    const highlights = [];
    const targetCount = seededIntegerInRange(`${seedBase}:count`, 4, 5);

    const addHighlight = (label) => {
      if (!label || highlights.includes(label) || highlights.length >= targetCount) return;
      highlights.push(label);
    };

    rules.forEach(([labels, pattern], index) => {
      if (!pattern.test(text)) return;
      const labelIndex = seededIntegerInRange(`${seedBase}:rule:${index}`, 0, labels.length - 1);
      addHighlight(labels[Math.min(labelIndex, labels.length - 1)]);
    });

    let guard = 0;
    while (highlights.length < targetCount && guard < fallback.length * 3) {
      const labelIndex = seededIntegerInRange(`${seedBase}:fallback:${guard}`, 0, fallback.length - 1);
      addHighlight(fallback[Math.min(labelIndex, fallback.length - 1)]);
      guard += 1;
    }

    if (highlights.length < targetCount) {
      fallback.forEach(addHighlight);
    }

    return highlights.slice(0, targetCount);
  };

  const createJudgeMeInsightMetric = ({ value, label, sublabel, modifier }) => {
    const metric = document.createElement('div');
    metric.className = `pawuni-jdgm-insight-metric${modifier ? ` pawuni-jdgm-insight-metric--${modifier}` : ''}`;

    const valueNode = document.createElement('span');
    valueNode.className = 'pawuni-jdgm-insight-metric__value';
    valueNode.textContent = value;

    const labelNode = document.createElement('span');
    labelNode.className = 'pawuni-jdgm-insight-metric__label';

    const labelText = document.createElement('span');
    labelText.textContent = label;
    labelNode.appendChild(labelText);

    if (sublabel) {
      const sublabelNode = document.createElement('span');
      sublabelNode.className = 'pawuni-jdgm-insight-metric__sublabel';
      sublabelNode.textContent = sublabel;
      labelNode.appendChild(sublabelNode);
    }

    metric.append(valueNode, labelNode);
    return metric;
  };

  const buildJudgeMeReviewInsightPanel = (root) => {
    if (!(root instanceof Element)) return;

    const header = root.querySelector('.jm-review-widget__header');
    if (!header) return;

    header
      .querySelector('.jm-review-widget-minimal-header__title, .jm-average-rating-display')
      ?.closest('.jm-stack')
      ?.classList.add('pawuni-jdgm-native-summary-stack');

    const summary = getJudgeMeReviewSummaryData(root);
    const highlights = getJudgeMeReviewHighlights(summary);
    const insightPanel = header.querySelector(`.${PAWUNI_JUDGE_ME_INSIGHT_PANEL_CLASS}`) || document.createElement('section');

    insightPanel.className = PAWUNI_JUDGE_ME_INSIGHT_PANEL_CLASS;
    insightPanel.setAttribute('aria-label', 'Review highlights');
    insightPanel.textContent = '';

    const title = document.createElement('h2');
    title.className = 'pawuni-jdgm-insight-panel__title';
    title.textContent = 'Reviews for this item';
    insightPanel.appendChild(title);

    const highlightsRow = document.createElement('div');
    highlightsRow.className = 'pawuni-jdgm-insight-highlights';
    const highlightsLabel = document.createElement('strong');
    highlightsLabel.textContent = 'Buyer highlights:';
    highlightsRow.appendChild(highlightsLabel);

    highlights.forEach((highlight) => {
      const item = document.createElement('span');
      item.className = 'pawuni-jdgm-insight-highlight';
      item.textContent = highlight;
      highlightsRow.appendChild(item);
    });
    const average = document.createElement('div');
    average.className = 'pawuni-jdgm-insight-average';

    const averageValue = document.createElement('span');
    averageValue.className = 'pawuni-jdgm-insight-average__value';
    averageValue.textContent = formatDecimalRating(summary.averageRating);

    const averageStar = document.createElement('span');
    averageStar.className = 'pawuni-jdgm-insight-average__star';
    averageStar.setAttribute('aria-hidden', 'true');
    averageStar.innerHTML = PAWUNI_REVIEW_STAR_SVG.repeat(5);

    const averageLabel = document.createElement('span');
    averageLabel.className = 'pawuni-jdgm-insight-average__label';
    const averageReviewCount = document.createElement('span');
    averageReviewCount.className = 'pawuni-jdgm-insight-average__count';
    averageReviewCount.textContent = `Based on ${formatJudgeMeReviewNoun(summary.reviewCount)}`;
    averageLabel.appendChild(averageReviewCount);

    average.append(averageValue, averageStar, averageLabel);
    insightPanel.appendChild(average);
    insightPanel.appendChild(highlightsRow);

    const recommendationSublabel =
      summary.recommendationPercent >= 100
        ? `All ${formatJudgeMeReviewNoun(summary.reviewCount)} would recommend this item`
        : `${summary.recommendationPercent}% of buyers would recommend this item`;

    const metrics = document.createElement('div');
    metrics.className = 'pawuni-jdgm-insight-metrics';

    [
      { value: getJudgeMeMetricDisplayRating(summary, 'Item quality'), label: 'Item quality' },
      { value: getJudgeMeMetricDisplayRating(summary, 'Shipping'), label: 'Shipping' },
      { value: getJudgeMeMetricDisplayRating(summary, 'Customer service'), label: 'Customer service' },
      { value: `${summary.recommendationPercent}%`, label: 'Buyers recommend', sublabel: recommendationSublabel },
    ].forEach((metric) => {
      metrics.appendChild(createJudgeMeInsightMetric({ ...metric, modifier: 'inline' }));
    });

    highlightsRow.appendChild(metrics);

    if (!insightPanel.parentNode) {
      header.prepend(insightPanel);
    }

    root.classList.add('pawuni-jdgm-insights-ready');
    root.dataset.pawuniJdgmAverageRating = formatDecimalRating(summary.averageRating);
    root.dataset.pawuniJdgmReviewCount = String(summary.reviewCount);
  };

  const syncJudgeMeReviewCardClasses = (root) => {
    if (!(root instanceof Element)) return;

    root.querySelectorAll(JUDGE_ME_REVAMP_CARD_SELECTOR).forEach((card) => {
      const hasMedia = Boolean(resolveJudgeMeReviewMedia(card));
      card.classList.toggle('pawuni-jdgm-review-card--has-media', hasMedia);
      card.classList.toggle('pawuni-jdgm-review-card--text-only', !hasMedia);
    });
  };

  const openJudgeMePhotoRailGallery = (mediaItems, startIndex) => {
    const fancybox = window.jQuery?.fancybox || window.$?.fancybox;
    if (!fancybox?.open || !Array.isArray(mediaItems) || mediaItems.length === 0) return false;

    const shouldUseNativeArrows =
      window.matchMedia?.('(max-width: 480px)').matches ||
      (document.documentElement.classList.contains('touchevents') && window.matchMedia?.('(max-width: 1024px)').matches);

    const galleryItems = mediaItems
      .map((item, index) => {
        const src = getJudgeMeReviewImageSrc(item.image, 1200);
        if (!src) return null;

        return {
          src,
          type: 'image',
          opts: {
            caption: item.customerName ? `Review photo from ${item.customerName}` : `Customer review photo ${index + 1}`,
          },
        };
      })
      .filter(Boolean);

    if (!galleryItems.length) return false;

    fancybox.open(
      galleryItems,
      {
        hash: false,
        loop: galleryItems.length > 1,
        smallBtn: true,
        toolbar: false,
        infobar: false,
        arrows: shouldUseNativeArrows,
        buttons: ['close'],
        mobile: {
          buttons: ['close'],
          clickContent: 'zoom',
          clickSlide: 'close',
          preventCaptionOverlap: false,
          toolbar: true,
        },
      },
      clampNumber(startIndex, 0, galleryItems.length - 1),
    );

    return true;
  };

  const buildJudgeMeReviewPhotoRail = (root) => {
    if (!(root instanceof Element)) return;

    const body = root.querySelector('.jm-review-widget__body') || root;
    const reviewCards = Array.from(root.querySelectorAll(JUDGE_ME_REVAMP_CARD_SELECTOR));
    const mediaItems = reviewCards
      .map((card) => {
        const media = resolveJudgeMeReviewMedia(card);
        if (!media) return null;
        return {
          card,
          image: media.image,
          clickTarget: media.clickTarget,
          customerName: getJudgeMeReviewCardName(card),
        };
      })
      .filter(Boolean)
      .slice(0, 10);

    let rail = root.querySelector(`.${PAWUNI_JUDGE_ME_PHOTO_RAIL_CLASS}`);

    if (mediaItems.length < 2) {
      rail?.remove();
      root.classList.remove('pawuni-jdgm-has-photo-rail');
      root.classList.remove('pawuni-jdgm-has-few-photos', 'pawuni-jdgm-has-many-photos');
      root.removeAttribute('data-pawuni-jdgm-photo-count');
      return;
    }

    if (!rail) {
      rail = document.createElement('section');
      rail.className = PAWUNI_JUDGE_ME_PHOTO_RAIL_CLASS;
      rail.setAttribute('aria-label', 'Customer review photos');

      const masonryGrid = body.querySelector('.jdgm-masonry-grid');
      if (masonryGrid) {
        masonryGrid.before(rail);
      } else {
        body.prepend(rail);
      }
    }

    root.classList.add('pawuni-jdgm-has-photo-rail');
    root.classList.toggle('pawuni-jdgm-has-few-photos', mediaItems.length < 4);
    root.classList.toggle('pawuni-jdgm-has-many-photos', mediaItems.length >= 4);
    root.dataset.pawuniJdgmPhotoCount = String(mediaItems.length);

    rail.classList.toggle(`${PAWUNI_JUDGE_ME_PHOTO_RAIL_CLASS}--compact`, mediaItems.length < 4);
    rail.classList.toggle(`${PAWUNI_JUDGE_ME_PHOTO_RAIL_CLASS}--gallery`, mediaItems.length >= 4);
    rail.dataset.pawuniJdgmPhotoCount = String(mediaItems.length);
    rail.textContent = '';

    const heading = document.createElement('div');
    heading.className = 'pawuni-jdgm-photo-rail__heading';

    const headingText = document.createElement('span');
    headingText.textContent = 'Customer photos';
    heading.appendChild(headingText);

    const headingCount = document.createElement('span');
    headingCount.className = 'pawuni-jdgm-photo-rail__count';
    headingCount.textContent = String(mediaItems.length);
    heading.appendChild(headingCount);

    rail.appendChild(heading);

    const scroller = document.createElement('div');
    scroller.className = 'pawuni-jdgm-photo-rail__scroller';
    rail.appendChild(scroller);

    mediaItems.forEach((item, index) => {
      const button = document.createElement('button');
      button.className = 'pawuni-jdgm-photo-rail__item';
      button.type = 'button';
      button.setAttribute(
        'aria-label',
        item.customerName
          ? `Open review photo from ${item.customerName}`
          : `Open review photo ${index + 1}`,
      );

      const image = document.createElement('img');
      image.alt = item.image.alt || (item.customerName ? `Review photo from ${item.customerName}` : 'Customer review photo');
      image.decoding = 'async';
      image.loading = 'lazy';
      image.src = getJudgeMeReviewImageSrc(item.image, 480);

      const srcset = item.image.getAttribute('srcset');
      if (srcset) image.setAttribute('srcset', srcset);

      button.appendChild(image);
      button.addEventListener('click', (event) => {
        event.preventDefault();

        if (openJudgeMePhotoRailGallery(mediaItems, index)) return;

        if (item.clickTarget instanceof HTMLElement && item.clickTarget !== item.card) {
          item.clickTarget.click();
          return;
        }

        item.card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (item.card instanceof HTMLElement) item.card.focus({ preventScroll: true });
      });

      scroller.appendChild(button);
    });
  };

  const enhanceJudgeMeProductReviewsLayout = (scope = document) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    scope.querySelectorAll(JUDGE_ME_PRODUCT_REVIEWS_SELECTOR).forEach((root) => {
      if (!(root instanceof Element)) return;

      syncJudgeMeReviewCardClasses(root);
      buildJudgeMeReviewPhotoRail(root);
      buildJudgeMeReviewInsightPanel(root);
    });
  };

  const initJudgeMeProductReviewsLayoutEnhancement = () => {
    if (!isProductContext(JUDGE_ME_PRODUCT_REVIEWS_SELECTOR)) return;

    const run = () => enhanceJudgeMeProductReviewsLayout(document);
    const rerunThrottled = createRafThrottled(run);

    run();
    runWithDelays(run, [300, 900, 1800, 3600, 6000]);

    if (window.__pawuniJudgeMeProductReviewsLayoutEnhanced) return;
    window.__pawuniJudgeMeProductReviewsLayoutEnhanced = true;

    const reviewRoot = document.querySelector(JUDGE_ME_PRODUCT_REVIEWS_SELECTOR) || document.body;
    const observer = new MutationObserver((mutations) => {
      const shouldRerun = mutations.some((mutation) => {
        if (!mutationsHaveAddedNodes([mutation])) return false;

        return Array.from(mutation.addedNodes || []).some((node) => {
          if (nodeMatchesOrContains(node, `.${PAWUNI_JUDGE_ME_PHOTO_RAIL_CLASS}`)) return false;
          return nodeMatchesOrContains(
            node,
            `${JUDGE_ME_PRODUCT_REVIEWS_SELECTOR}, ${JUDGE_ME_REVAMP_CARD_SELECTOR}, .jdgm-masonry-grid, .jm-review-widget__body`,
          );
        });
      });

      if (shouldRerun) rerunThrottled();
    });

    observer.observe(reviewRoot, { childList: true, subtree: true });
    bindJudgeMeAndTeeinblueEvents(rerunThrottled);
  };

  const TEE_OPTION_SELECTOR = '.tee-option';
  const TEE_OPTION_TITLE_SELECTOR = '.tee-option__title';
  const TEE_OPTION_VALUE_SELECTOR = '.tee-radio-label, .tee-option-value, .tee-option-item-label';
  const TEE_OPTION_SIZE_VALUE_PATTERN = /^(xxs|xs|s|m|l|xl|xxl|2xl|3xl|4xl|5xl)$/i;
  const normalizeTeeOptionLabel = (value) =>
    normalizeInlineText(value)
      .toLowerCase()
      .replace(/[_:|-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const applyTeeinblueFieldAnnotations = () => {
    const scope = document.querySelector(PRODUCT_DETAILS_SELECTOR);
    if (!scope) return;

    const options = Array.from(scope.querySelectorAll(TEE_OPTION_SELECTOR));

    options.forEach((option) => {
      option.classList.remove('pawuni-tee-option-color', 'pawuni-tee-option-size');
      const title = option.querySelector(TEE_OPTION_TITLE_SELECTOR);
      if (!title) return;

      const normalizedLabel = normalizeTeeOptionLabel(title.textContent);
      if (normalizedLabel.includes('color')) option.classList.add('pawuni-tee-option-color');
      if (normalizedLabel.includes('size')) option.classList.add('pawuni-tee-option-size');
    });

    if (!scope.querySelector('.tee-option.pawuni-tee-option-color')) {
      const fallbackColorOption = options.find((option) => {
        const titleText = normalizeInlineText(option.querySelector(TEE_OPTION_TITLE_SELECTOR)?.textContent || '').toLowerCase();
        return titleText.includes('color');
      });
      if (fallbackColorOption) fallbackColorOption.classList.add('pawuni-tee-option-color');
    }

    if (!scope.querySelector('.tee-option.pawuni-tee-option-size')) {
      const fallbackSizeOption =
        options.find((option) => {
          const titleText = normalizeInlineText(option.querySelector(TEE_OPTION_TITLE_SELECTOR)?.textContent || '').toLowerCase();
          return titleText.includes('size');
        }) ||
        options.find((option) => {
          const labels = Array.from(option.querySelectorAll(TEE_OPTION_VALUE_SELECTOR)).map((node) =>
            normalizeInlineText(node.textContent),
          );
          return labels.some((text) => TEE_OPTION_SIZE_VALUE_PATTERN.test(text));
        });
      if (fallbackSizeOption) fallbackSizeOption.classList.add('pawuni-tee-option-size');
    }

    [
      ['.pawuni-size-chart-link', 'pawuni-size-chart-link'],
      ['.pawuni-size-chart-row', 'pawuni-size-chart-row'],
      ['.pawuni-size-chart-duplicate', 'pawuni-size-chart-duplicate'],
      ['.pawuni-size-chart-source-hidden', 'pawuni-size-chart-source-hidden'],
      ['.pawuni-tee-available-block', 'pawuni-tee-available-block'],
      ['.pawuni-tee-ap-name-row', 'pawuni-tee-ap-name-row'],
    ].forEach(([selector, className]) => {
      removeClassFromAll(scope, selector, className);
    });
    removeAll(scope, '.pawuni-size-chart-proxy');
    unwrapAll(scope, '.pawuni-size-chart-inline');

    scope.querySelectorAll('.tee-field--teeinblue-available-prod').forEach((block) => {
      block.classList.add('pawuni-tee-available-block');
    });

    scope.querySelectorAll('.tee-ap-thumbnail__name').forEach((nameNode) => {
      const row =
        nameNode.closest('.tee-ap-thumbnail-name-wrapper') ||
        nameNode.parentElement ||
        nameNode.closest('div') ||
        nameNode;
      row.classList.add('pawuni-tee-ap-name-row');
    });

    applySyntheticJudgeMePreviewCounts(scope);
  };

  const initTeeinblueFieldAnnotations = () => {
    if (!isProductContext(PRODUCT_DETAILS_SELECTOR)) return;

    const run = () => applyTeeinblueFieldAnnotations();
    const rerunThrottled = createRafThrottled(run);

    run();

    if (window.__pawuniTeeinblueFieldAnnotationsInitialized) return;
    window.__pawuniTeeinblueFieldAnnotationsInitialized = true;

    runWithDelays(run, [600, 1800, 3600]);

    const detailsContainer = document.querySelector(PRODUCT_DETAILS_SELECTOR) || document.body;

    const observer = new MutationObserver((mutations) => {
      if (!mutationsHaveAddedNodes(mutations)) return;
      rerunThrottled();
    });

    observer.observe(detailsContainer, { childList: true, subtree: true });
    bindJudgeMeAndTeeinblueEvents(rerunThrottled);
  };

  const isTeeinblueProductFlagEnabled = () => {
    const flag = window.TeeInBlueCampaign?.isTeeInBlueProduct;
    return flag === true || String(flag).toLowerCase() === 'true';
  };

  const PDP_CUSTOM_PANEL_CLASS = 'pawuni-pdp-custom-panel';
  const PDP_CUSTOM_PANEL_EMPTY_CLASS = 'pawuni-pdp-custom-panel--empty';
  const TEEINBLUE_READY_SELECTOR =
    '.teeinblue-product-form, .tee-form-wrapper, .tee-customization-wrapper, .tee-option, .tee-field, .tee-form-actions, .tee-btn';

  const isTeeinblueProductFormBlock = (node) => {
    if (!(node instanceof Element)) return false;
    if (!node.classList.contains('product-block--form')) return false;

    if (
      node.querySelector(
        '.teeinblue-product-form, .tee-form-wrapper, .tee-customization-wrapper, .tee-option, .tee-field, .tee-form-actions',
      )
    ) {
      return true;
    }

    return document.body?.classList.contains('teeinblue-enabled') || isTeeinblueProductFlagEnabled();
  };

  const updatePdpCustomizationPanelState = (panel) => {
    if (!(panel instanceof Element)) return;

    panel.classList.toggle(PDP_CUSTOM_PANEL_EMPTY_CLASS, !panel.querySelector(TEEINBLUE_READY_SELECTOR));
  };

  const applyPdpCustomizationPanel = () => {
    const detailsContainer = document.querySelector(PRODUCT_DETAILS_SELECTOR);
    if (!detailsContainer) return;

    const directChildren = Array.from(detailsContainer.children);
    let panel = directChildren.find((node) => node.classList?.contains(PDP_CUSTOM_PANEL_CLASS));
    const formBlock =
      directChildren.find(isTeeinblueProductFormBlock) ||
      Array.from(panel?.children || []).find(isTeeinblueProductFormBlock);

    if (!formBlock) return;

    if (!panel) {
      panel = document.createElement('div');
      panel.className = PDP_CUSTOM_PANEL_CLASS;
      panel.dataset.pawuniPdpCustomPanel = 'true';
      formBlock.before(panel);
    }

    Array.from(detailsContainer.children).forEach((node) => {
      if (node === panel) return;
      if (isTeeinblueProductFormBlock(node) || node.classList?.contains('product-block--payment_terms')) {
        panel.appendChild(node);
      }
    });

    updatePdpCustomizationPanelState(panel);
  };

  const initPdpCustomizationPanel = () => {
    if (!isProductContext(PRODUCT_DETAILS_SELECTOR)) return;

    const run = () => applyPdpCustomizationPanel();
    const rerunThrottled = createRafThrottled(run);

    run();
    runWithDelays(run, [180, 600, 1500, 3600]);

    if (window.__pawuniPdpCustomizationPanelBound) return;
    window.__pawuniPdpCustomizationPanelBound = true;

    const detailsContainer = document.querySelector(PRODUCT_DETAILS_SELECTOR) || document.body;
    const observer = new MutationObserver((mutations) => {
      if (!mutationsHaveAddedNodes(mutations)) return;
      rerunThrottled();
    });

    observer.observe(detailsContainer, { childList: true, subtree: true });
    bindJudgeMeAndTeeinblueEvents(rerunThrottled);
  };

  const isStandardShopifyProductContext = () => {
    if (!hasBodyClassMatch(/\btemplate-product\b/) && !window.location.pathname.includes('/products/')) return false;
    return !isTeeinblueProductFlagEnabled();
  };

  const unwrapLayoutContainer = (detailsContainer, selector) => {
    const container = detailsContainer.querySelector(selector);
    if (!container || !container.parentNode) return;

    while (container.firstChild) {
      container.parentNode.insertBefore(container.firstChild, container);
    }
    container.remove();
  };

  const uniqueNodes = (nodes) => nodes.filter((node, index, list) => node && list.indexOf(node) === index);

  const resolveProductBlockNode = (detailsContainer, selector) => {
    const node = detailsContainer.querySelector(selector);
    if (!node) return null;
    if (node.classList?.contains('product-block')) return node;
    return node.closest('.product-block') || node;
  };

  const hasDiscountPriceAppContent = (appBlock) => {
    if (!(appBlock instanceof Element)) return false;

    if (
      appBlock.querySelector(
        '[data-discount-price-root], .bb-discount-price, .bb-discount-price__headline, .bb-discount-price__sale, .bb-discount-price__compare, .bb-discount-price__meta',
      )
    ) {
      return true;
    }

    const appShell = appBlock.querySelector('.shopify-block.shopify-app-block') || appBlock;
    const text = normalizeInlineText(appShell.textContent || '');
    if (/(?:[$€£¥]|usd|cad|aud|eur)\s*\d/i.test(text)) return true;
    if (/\bnow\b/i.test(text) && /\d/.test(text)) return true;
    if (/\boff\b/i.test(text) && /\d/.test(text)) return true;

    return Array.from(appShell.children).some((node) => {
      if (!(node instanceof Element)) return false;
      if (node.matches('script, style, link, template')) return false;
      if (node.classList.contains('shopify-app-block') && node.childElementCount === 0 && !normalizeInlineText(node.textContent)) {
        return false;
      }
      return node.childElementCount > 0 || normalizeInlineText(node.textContent).length > 0;
    });
  };

  const PDP_MARKETING_TIMING_TEXT = 'Hurry, offer ends today';
  const PDP_MARKETING_TEXT_SELECTOR = '.bb-discount-price__timing';
  const PDP_MARKETING_APP_SELECTOR = `${DISCOUNT_PRICE_APP_SELECTOR}, .bb-discount-price`;

  const normalizePdpMarketingLabels = (scope = document) => {
    if (!isProductContext(PRODUCT_DETAILS_SELECTOR)) return;
    if (!(scope instanceof Element || scope instanceof Document)) return;

    scope.querySelectorAll(PDP_MARKETING_TEXT_SELECTOR).forEach((node) => {
      if (!(node instanceof Element)) return;

      if (normalizeInlineText(node.textContent || '') !== PDP_MARKETING_TIMING_TEXT) {
        node.textContent = PDP_MARKETING_TIMING_TEXT;
      }

      node.dataset.pawuniMarketingTextNormalized = 'true';
    });
  };

  const initPdpMarketingLabelsNormalization = () => {
    if (!isProductContext(PRODUCT_DETAILS_SELECTOR)) return;

    const run = () => normalizePdpMarketingLabels(document);
    const rerunThrottled = createRafThrottled(run);

    run();
    runWithDelays(run, [180, 600, 1500, 3600, 6000]);

    if (window.__pawuniPdpMarketingLabelsNormalized) return;
    window.__pawuniPdpMarketingLabelsNormalized = true;

    const detailsContainer = document.querySelector(PRODUCT_DETAILS_SELECTOR) || document.body;
    const observer = new MutationObserver((mutations) => {
      const shouldRerun = mutations.some((mutation) => {
        const target = mutation.target instanceof Element ? mutation.target : mutation.target?.parentElement;

        if (target?.closest?.(PDP_MARKETING_APP_SELECTOR)) return true;

        return Array.from(mutation.addedNodes || []).some((node) => {
          return nodeMatchesOrContains(node, PDP_MARKETING_APP_SELECTOR);
        });
      });

      if (shouldRerun) rerunThrottled();
    });

    observer.observe(detailsContainer, { childList: true, subtree: true, characterData: true });
    bindJudgeMeAndTeeinblueEvents(rerunThrottled);
  };

  const syncDiscountPriceVisibility = (detailsContainer) => {
    if (!(detailsContainer instanceof Element)) return;

    const appBlocks = Array.from(detailsContainer.querySelectorAll(DISCOUNT_PRICE_APP_SELECTOR));
    if (appBlocks.length === 0) {
      detailsContainer.classList.remove('pawuni-discount-price-active');
      return;
    }

    let hasActiveDiscountPrice = false;

    appBlocks.forEach((appBlock) => {
      const isActive = hasDiscountPriceAppContent(appBlock);
      appBlock.classList.toggle('pawuni-discount-price-app-active', isActive);
      hasActiveDiscountPrice = hasActiveDiscountPrice || isActive;
    });

    detailsContainer.classList.toggle('pawuni-discount-price-active', hasActiveDiscountPrice);
  };

  const applyDiscountPriceVisibility = () => {
    document.querySelectorAll(PRODUCT_DETAILS_SELECTOR).forEach((detailsContainer) => {
      syncDiscountPriceVisibility(detailsContainer);
    });
  };

  const appendUniqueNodes = (target, nodes) => {
    uniqueNodes(nodes).forEach((node) => {
      if (!node || node === target) return;
      target.appendChild(node);
    });
  };

  const reorderStandardShopifyProductLayout = () => {
    if (!isStandardShopifyProductContext()) return;

    const detailsContainer = document.querySelector(PRODUCT_DETAILS_SELECTOR);
    if (!detailsContainer) return;

    document.body?.classList.add('pawuni-standard-shopify-product');
    detailsContainer.classList.add('pawuni-standard-shopify-product');

    detailsContainer.querySelectorAll('.modal_price .savings, .product-block--price .savings').forEach((node) => {
      node.remove();
    });
    detailsContainer.querySelectorAll('.product-block--sale_off_text, .sale-off-text').forEach((node) => {
      const saleOffBlock = node.classList?.contains('product-block') ? node : node.closest('.product-block--sale_off_text');
      if (saleOffBlock) {
        saleOffBlock.remove();
      } else {
        node.remove();
      }
    });

    const title = detailsContainer.querySelector('.product-block--title .product_name') || detailsContainer.querySelector('.product_name');
    if (!title) return;

    const layoutMode = resolvePdpLayoutMode();

    unwrapLayoutContainer(detailsContainer, '.container-before-product-title');
    unwrapLayoutContainer(detailsContainer, '.container-after-product-title');

    detailsContainer.querySelectorAll('.reference-products-accordion').forEach((accordion) => {
      const refs = accordion.querySelectorAll('.reference-product');
      refs.forEach((ref) => {
        detailsContainer.appendChild(ref);
      });
      accordion.remove();
    });

    const reviewBlocks = uniqueNodes([
      resolveProductBlockNode(detailsContainer, '.product-block--rating-and-badge'),
      resolveProductBlockNode(detailsContainer, '.product-block--rating'),
      resolveProductBlockNode(detailsContainer, '.product-block--custom-rating'),
      resolveProductBlockNode(detailsContainer, '.jdgm-preview-badge'),
      resolveProductBlockNode(detailsContainer, '.shopify-product-reviews-badge'),
      resolveProductBlockNode(detailsContainer, '.spr-badge'),
    ]);

    const shippingAndReturnBlock = resolveProductBlockNode(detailsContainer, '.shipping-and-return-policies--container');
    const estimatedDeliveryBlock = resolveProductBlockNode(detailsContainer, '.eb-estimate-wrapper');
    const priceBlock = resolveProductBlockNode(
      detailsContainer,
      `${DISCOUNT_PRICE_APP_SELECTOR}, .modal_price, .tee-product-price, [data-price-ui]`,
    );
    const cutOffDateByContentBlock = resolveProductBlockNode(detailsContainer, '.cut-off-date-by-content');
    const cutOffDateByDateBlock = resolveProductBlockNode(detailsContainer, '.cut-off-date-by-date');
    const giftWithPurchaseNoticeBlock = resolveProductBlockNode(detailsContainer, '.gift-with-purchase-notice--container');
    const referenceBlocks = Array.from(detailsContainer.querySelectorAll('.product-block--reference_product'));

    const containerBeforeTitle = document.createElement('div');
    containerBeforeTitle.className = 'container-before-product-title';

    const containerAfterTitle = document.createElement('div');
    containerAfterTitle.className = 'container-after-product-title';

    const appendTrailingBlocks = (target) => {
      appendUniqueNodes(target, [cutOffDateByContentBlock, cutOffDateByDateBlock, giftWithPurchaseNoticeBlock, ...referenceBlocks]);
    };

    if (layoutMode === '2') {
      if (shippingAndReturnBlock) {
        containerBeforeTitle.appendChild(shippingAndReturnBlock);
      } else if (estimatedDeliveryBlock) {
        containerBeforeTitle.appendChild(estimatedDeliveryBlock);
      }

      if (priceBlock) {
        containerBeforeTitle.appendChild(priceBlock);
      }

      if (containerBeforeTitle.childElementCount > 0) {
        title.before(containerBeforeTitle);
      }

      appendUniqueNodes(containerAfterTitle, reviewBlocks);
      appendTrailingBlocks(containerAfterTitle);

      if (containerAfterTitle.childElementCount > 0) {
        title.after(containerAfterTitle);
      }
      return;
    }

    appendUniqueNodes(containerAfterTitle, reviewBlocks);

    if (priceBlock) {
      containerAfterTitle.appendChild(priceBlock);
    }

    if (shippingAndReturnBlock) {
      containerAfterTitle.appendChild(shippingAndReturnBlock);
    } else if (estimatedDeliveryBlock) {
      containerAfterTitle.appendChild(estimatedDeliveryBlock);
    }

    appendTrailingBlocks(containerAfterTitle);

    if (containerAfterTitle.childElementCount > 0) {
      title.after(containerAfterTitle);
    }
  };

  const initStandardShopifyProductLayout = () => {
    if (!isStandardShopifyProductContext()) return;

    const run = () => reorderStandardShopifyProductLayout();

    run();
    runWithDelays(run, [180, 600, 1500]);

    if (window.__pawuniStandardShopifyProductLayoutBound) return;
    window.__pawuniStandardShopifyProductLayoutBound = true;

    let isApplyingLayout = false;
    const runSafely = () => {
      if (isApplyingLayout) return;
      isApplyingLayout = true;
      try {
        run();
      } finally {
        isApplyingLayout = false;
      }
    };
    const rerunThrottled = createRafThrottled(runSafely, () => isApplyingLayout);

    const detailsContainer = document.querySelector(PRODUCT_DETAILS_SELECTOR) || document.body;
    const isInlinePopupNode = (node) => {
      if (!(node instanceof Element)) return false;
      if (node.classList.contains('popup-inline')) return true;
      if (node.closest('.popup-inline')) return true;
      return node.matches('.popup-modal, .popup-modal__dialog, .popup-modal__overlay, .popup-modal__close');
    };
    const isPopupOnlyMutation = (mutation) => {
      if (!(mutation.target instanceof Element)) return false;
      if (!mutation.target.closest('.popup-modal--container')) return false;

      const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes].filter((node) => node instanceof Element);
      if (changedNodes.length === 0) return true;
      return changedNodes.every(isInlinePopupNode);
    };

    const observer = new MutationObserver((mutations) => {
      if (isApplyingLayout) return;

      const shouldRerun = mutations.some((mutation) => {
        if (mutation.type !== 'childList') return false;
        if (isPopupOnlyMutation(mutation)) return false;
        return mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
      });

      if (shouldRerun) rerunThrottled();
    });

    observer.observe(detailsContainer, { childList: true, subtree: true });
    bindJudgeMeAndTeeinblueEvents(rerunThrottled);
  };

  const initDiscountPriceVisibility = () => {
    if (!isProductContext(PRODUCT_DETAILS_SELECTOR)) return;

    const run = () => applyDiscountPriceVisibility();
    const rerunThrottled = createRafThrottled(run);

    run();
    runWithDelays(run, [180, 600, 1500, 3600]);

    if (window.__pawuniDiscountPriceVisibilityBound) return;
    window.__pawuniDiscountPriceVisibilityBound = true;

    const detailsContainer = document.querySelector(PRODUCT_DETAILS_SELECTOR) || document.body;
    const observer = new MutationObserver((mutations) => {
      const shouldRerun = mutations.some((mutation) => {
        if (mutation.type !== 'childList') return false;
        if (!(mutation.target instanceof Element)) return false;
        if (mutation.target.closest(DISCOUNT_PRICE_APP_SELECTOR)) return true;
        return Array.from(mutation.addedNodes || []).some((node) => {
          return nodeMatchesOrContains(node, DISCOUNT_PRICE_APP_SELECTOR);
        });
      });

      if (shouldRerun) rerunThrottled();
    });

    observer.observe(detailsContainer, { childList: true, subtree: true });
    bindJudgeMeAndTeeinblueEvents(rerunThrottled);
  };

  const syncPdpCopyToggle = (toggle) => {
    if (!(toggle instanceof HTMLElement)) return;

    const content = toggle.querySelector('[data-pawuni-pdp-copy-content]');
    const control = toggle.querySelector('[data-pawuni-pdp-copy-toggle-button]');
    if (!(content instanceof HTMLElement) || !(control instanceof HTMLButtonElement)) return;
    if (content.getClientRects().length === 0) return;

    const wasExpanded = toggle.classList.contains('is-expanded');
    if (wasExpanded) {
      toggle.classList.remove('is-expanded');
    }

    const collapsedHeight = content.clientHeight;
    const fullHeight = content.scrollHeight;
    const isClampable = fullHeight > collapsedHeight + 2;

    toggle.classList.toggle('is-clampable', isClampable);
    control.hidden = !isClampable;

    if (isClampable && wasExpanded) {
      toggle.classList.add('is-expanded');
    }

    if (!isClampable) {
      toggle.classList.remove('is-expanded');
    }

    control.setAttribute('aria-expanded', toggle.classList.contains('is-expanded') ? 'true' : 'false');
  };

  const initPdpCopyToggle = () => {
    if (!isProductContext('.pawuni-pdp-design-tabs-container')) return;

    const syncAll = () => {
      document.querySelectorAll('[data-pawuni-pdp-copy-toggle]').forEach(syncPdpCopyToggle);
    };
    const syncAllThrottled = createRafThrottled(syncAll);

    syncAll();
    runWithDelays(syncAll, [180, 600, 1500]);

    if (window.__pawuniPdpCopyToggleBound) return;
    window.__pawuniPdpCopyToggleBound = true;

    document.addEventListener('click', (event) => {
      const control = event.target instanceof Element ? event.target.closest('[data-pawuni-pdp-copy-toggle-button]') : null;
      if (!(control instanceof HTMLButtonElement)) return;

      const toggle = control.closest('[data-pawuni-pdp-copy-toggle]');
      if (!(toggle instanceof HTMLElement)) return;

      toggle.classList.toggle('is-expanded');
      control.setAttribute('aria-expanded', toggle.classList.contains('is-expanded') ? 'true' : 'false');
    });

    document.addEventListener('change', (event) => {
      const tabRadio = event.target instanceof Element ? event.target.closest('.pawuni-pdp-design-tabs__radio') : null;
      if (!tabRadio) return;

      syncAllThrottled();
    });

    window.addEventListener('resize', syncAllThrottled);
  };

  const runProductHotfixes = () => {
    applyPdpLayoutModeClass();
    initTeeinblueFieldAnnotations();
    initPdpCustomizationPanel();
    initPdpCopyToggle();
    initDiscountPriceVisibility();
    initPdpMarketingLabelsNormalization();
    initStandardShopifyProductLayout();
    initSyntheticJudgeMePreviewCounts();
    initJudgeMeOtherProductsReviewsHeading();
    initJudgeMeReviewActionsNormalization();
    initJudgeMeProductReviewsLayoutEnhancement();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runProductHotfixes);
  } else {
    runProductHotfixes();
  }
})();
